/**
 * @fileoverview Account apps UI — the English (EN) bundle (APP-ENTITLEMENT-001 Phase 9).
 * @package @puffin-core/account-apps-ui
 *
 * Born on the namespace mechanism: `accountAppsUi`, registered once for every app by `@puffin-core/i18n-namespaces`,
 * so no host wires any copy. The sibling `es.js` is the Spanish bundle and `_meta.js` records the provenance of every
 * leaf in both. Placeholders use SINGLE braces (`{price}`), the interpolator `useAccountAppsUiTranslate` binds.
 *
 * Plain language on purpose: the person reading runs a store, not a billing system. A price is always the published
 * one, a trial is always fourteen days without a card, and every refusal says what to do next.
 */

export const ACCOUNT_APPS_UI_EN = Object.freeze({
  // The page
  heading: 'Apps and billing',
  anchorHeading: 'Your anchor',
  appsHeading: 'Apps',
  billingHeading: 'Billing',
  loading: 'Loading your apps…',
  loadFailed: 'Your apps could not be loaded. Try again.',
  retry: 'Try again',
  noAnchor: 'This account has no anchor app yet. Ask ParcelPuffin to set one up.',
  noPermission: 'Ask someone who manages apps for your account.',
  readOnlyBanner:
    'Some apps are read-only: their trials ended. Keep each one on a card to start working in it again.',
  waivedNote: 'Complimentary apps are never billed.',

  // Every app's state (`ENTITLEMENT_STATUS` and what the catalog says about an app the account lacks)
  stateActive: 'Active',
  stateTrialing: 'Trial',
  stateReadOnly: 'Read-only',
  stateAvailable: 'Available',
  stateComingSoon: 'Coming soon',
  stateIncluded: 'Included',
  stateComplimentary: 'Complimentary',
  stateOff: 'Off',
  trialEndsOn: 'Trial ends',
  trialEnded: 'The trial ended. Keep it on a card to use it again.',
  comingSoonHint: 'Not for sale yet. We will tell you when it is.',
  onlyWithAnchor: 'Goes with {anchors}',

  // Prices (the published ones; `{price}` is already formatted money)
  pricePerUnitMonthly: '{price} per {unit} a month',
  priceFlatMonthly: '{price} a month',
  pricePerBlock: '{price} per {blockSize} {units}',
  free: 'Free with your anchor',
  shareOfRecovery: '{percent}% of recovered refunds, no monthly fee',
  includedUnits: '{count} {units} included',
  priceUnpublished: 'Price not published yet',

  // Size dimensions, singular and plural
  unitAccount: 'account',
  unitLocation: 'location',
  unitRegister: 'register',
  unitSite: 'site',
  unitLine: 'phone line',
  unitAiReceptionist: 'AI receptionist',
  unitMailbox: 'mailbox',
  unitEmployeePaid: 'employee paid',
  unitStorefront: 'storefront',
  unitOffice: 'office',
  unitTechnician: 'technician',
  unitsAccount: 'account',
  unitsLocation: 'locations',
  unitsRegister: 'registers',
  unitsSite: 'sites',
  unitsLine: 'phone lines',
  unitsAiReceptionist: 'AI receptionists',
  unitsMailbox: 'mailboxes',
  unitsEmployeePaid: 'employees paid',
  unitsStorefront: 'storefronts',
  unitsOffice: 'offices',
  unitsTechnician: 'technicians',

  // Consumables (included up to a quantity, then whole blocks under a cap)
  consumableBlogPosts: 'blog posts',
  consumableAiMinutes: 'AI minutes',
  consumableSmsSegments: 'text segments',
  consumableAiGenerations: 'AI generations',
  consumableAiQualifications: 'AI qualifications',
  consumableAiOutcomes: 'AI outcomes',

  // Actions
  startTrial: 'Start a {days}-day trial',
  addApp: 'Add',
  turnOn: 'Turn on',
  turnOff: 'Turn off',
  keep: 'Keep it',
  remove: 'Remove',
  manageLines: 'Lines and caps',
  openApp: 'Open',
  working: 'Working…',
  cancel: 'Cancel',
  confirm: 'Confirm',
  save: 'Save',
  close: 'Close',

  // Confirming a change
  addTitle: 'Add {app}?',
  addTrialBody:
    'Your {days}-day trial starts now. No card is needed. When it ends, {app} is read-only until you keep it on a card.',
  addChargeBody: '{app} is {monthly} a month, billed with your next invoice.',
  proratedToday: 'Today: {amount} for the rest of this period.',
  periodEnd: 'Next invoice on',
  keepTitle: 'Keep {app}?',
  keepBody: 'Your card is charged {monthly} a month from now on.',
  keepNoCard: 'Add a card first, then keep the app.',
  removeTitle: 'Remove {app}?',
  removeBody: 'Its screens close and it stops being billed. Your data stays.',
  removePhrase: 'REMOVE',
  removePrompt: 'Type {phrase} to confirm.',
  turnOffTitle: 'Turn off {app}?',
  turnOffBody: 'Shoppers can no longer sign in to this account’s site.',
  previewFailed: 'The price could not be worked out. Try again.',

  // Lines and caps
  linesTitle: '{app}: lines and caps',
  derivedQuantityHint:
    'Counted from your account: add or remove one in {app} and this line follows.',
  declaredQuantityLabel: 'How many {units}',
  includedWithParent: '{count} {units} included with each {parentUnit}',
  capLabel: 'Cap on extra {consumable}',
  capHint:
    '{included} {consumable} included per {parentUnit}; more come in blocks of {blockSize}. Set the most blocks you will pay for in a month. 0 stops at what is included.',
  capBlocks: 'blocks',
  lineSaved: 'Saved.',

  // Refusals, worded by CODE (the server's sentence is never shown)
  refusalAnchorRequired: 'Choose an anchor app first.',
  refusalAppNotAttachable: 'This app does not go with your anchor.',
  refusalAppNotSellable: 'This app is not offered.',
  refusalAppUnknown: 'No such app.',
  refusalDimensionUnknown: 'This app has no such line.',
  refusalQuantityBelowIncluded: 'That is fewer than your anchor includes.',
  refusalPriceNotPublished: 'This app has no published price yet.',
  refusalPriceEndingRefused: 'The price could not be applied.',
  refusalCapReached: 'The cap for this period has been reached.',
  refusalMeterUnavailable: 'This usage is counted by the app itself. Change the cap from there.',
  refusalCapabilityDependencyUnmet: 'This change leaves an app without something it depends on.',
  refusalCapBelowConsumed: 'A cap cannot drop below what was used this period.',
  refusalAlreadyEntitled: 'You already have this app.',
  refusalNotEntitled: 'You do not have this app.',
  refusalNotTrialing: 'This app is not on a trial.',
  refusalNoPaymentMethod: 'Add a card first.',
  refusalNoBillingHead: 'This account has no billing record. Ask ParcelPuffin.',
  refusalComplimentaryEntitlement: 'This app is complimentary. Ask ParcelPuffin to change it.',
  refusalIncludedAppNotBillable: 'This app is free with your anchor.',
  refusalIncludedAppFollowsAnchor: 'This app comes with your anchor and goes with it.',
  refusalAnchorRemovalBlocked: 'Remove the other apps before the anchor.',
  refusalPriceConfirmationRequired: 'Confirm the price first.',
  // MAILBOX-SIGNUP-JOURNEY-001 B3: a change that starts or grows a recurring charge, shown before it is made.
  pendingChargeMonthly: 'This change adds {price} a month to the account’s bill.',
  pendingChargeAnnual: 'This change adds {price} a year to the account’s bill.',
  acceptCharge: 'Accept the charge',
  refusalComplimentaryAccount:
    'This account is complimentary: it takes no card and no billing cycle.',
  refusalBillingCycleUnchanged: 'You are already billed on that cycle.',
  refusalCardDeclined: 'The card was declined.',
  refusalCustomerAccountsInUse:
    'Customers have signed in to this account’s site, so customer accounts stay on.',
  refusalForbidden: 'You do not have permission to change this account’s apps.',
  refusalUnknown: 'The change was not made. Try again.',

  // Billing
  paymentMethodHeading: 'Payment method',
  cardSummary: '{brand} ending in {last4} (expires {expMonth}/{expYear})',
  noPaymentMethod: 'No card on file.',
  complimentaryNoCard: 'No card is needed: this account is complimentary.',
  stripeUnavailable: 'Card and invoice details are unavailable right now.',
  addCard: 'Add card',
  addCardDescription:
    'Your card is charged for the apps you keep after their trials, at the published price.',
  cardAria: 'Credit or debit card',
  cardPlaceholder: 'Card number, MM/YY, CVC',
  cardHelp: 'Card details go straight to the card processor and never reach this app.',
  saveCard: 'Save card',
  savingCard: 'Saving card…',
  cardSaveFailed: 'The card could not be saved. Try again.',
  cardNotReady: 'Card entry is not ready. Refresh and try again.',
  cardNoElement: 'Card entry is unavailable. Refresh and try again.',
  cardEntryUnconfigured: 'Card entry is not set up for this app yet.',
  cycleHeading: 'Billing cycle',
  cycleMonthly: 'Monthly',
  cycleAnnual: 'Annual',
  cycleAnnualNote: 'Annual is ten months’ price for twelve months.',
  switchToMonthly: 'Switch to monthly',
  switchToAnnual: 'Switch to annual',
  cycleChanged: 'Billing cycle changed.',
  nextInvoiceHeading: 'Your next invoice',
  quoteMonthly: '{amount} a month',
  quoteAnnual: '{amount} a year ({monthly} a month)',
  quoteUnavailable: 'A total cannot be shown yet: {reason}',
  invoicesHeading: 'Recent invoices',
  noInvoices: 'No invoices yet.',
  colDate: 'Date',
  colInvoice: 'Invoice',
  colStatus: 'Status',
  colAmount: 'Amount',
  colReceipt: 'Receipt',
  view: 'View',

  // What goes well with what the account has (`app_catalog_suggestions.reasonKey`)
  suggestionsHeading: 'Goes well with what you have',
  suggestionMailOnYourDomain:
    'Email at your own domain: ProntoPuffin mailboxes for the people behind your storefront.',
  suggestionAnswerEveryCall:
    'Answer every call: the AI receptionist takes the calls your team cannot.',
  suggestionSchedulesIntoPayroll:
    'Schedules straight into payroll: PlannerPuffin’s hours become PayDayPuffin’s timecards.',
  suggestionAction: 'Add {app}',

  // The anchor card: what it includes, by capability key
  anchorIncludesHeading: 'Included with {anchor}',
  freeAppsHeading: 'Free with your anchor',
  featurePos: 'Point of sale',
  featureWebsite: 'A website on your own domain',
  featureHosting: 'Hosting',
  featureBlog: 'Auto-blog',
  featureOwnerConsole: 'Owner console',
  featureAnalytics: 'Analytics',
  featurePageAccess: 'Gated pages and a customer portal',
  featureFieldService: 'Field service',
});
